import { setRenderLang } from "@/lib/pt-localize";
import { AccessibilityMenu } from "@/components/accessibility-menu";
import { LocalizedHeadContent } from "@/components/localized-head";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,

  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import { BackButton } from "../components/back-button";
import { ImageLightbox } from "../components/image-lightbox";
import appCss from "../styles.css?url";
import { I18nProvider } from "../lib/i18n";
import { LangOffer } from "../components/lang-offer";
import { langLinks, RTL_LANGS, type Lang, type SiteLang } from "../lib/lang-url";
import { reportLovableError } from "../lib/lovable-error-reporting";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: import("@tanstack/react-router").ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
  /** The language the visitor's address asks for. */
  getLang: () => SiteLang;
}>()({
  head: ({ matches }) => {
    // Canonical and hreflang for whichever page is being served, aimed at the
    // address of the language that address asks for.
    const leaf = matches[matches.length - 1];
    const lang = matches[0]?.context.getLang() ?? "en";
    return {
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "author", content: "Luiza Jesus do Prado" },
      { name: "google-site-verification", content: "4pGtc3JXBsVxm_QKNiTVAXc3zuPQFtF-DVt54kt3hQw" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Luiza Jesus do Prado" },
      { property: "og:locale", content: { en: "en_US", pt: "pt_BR", es: "es_ES", de: "de_DE", fr: "fr_FR", ja: "ja_JP", he: "he_IL", ar: "ar_AR", zh: "zh_CN", hi: "hi_IN", ru: "ru_RU", sw: "sw_KE", yo: "yo_NG", ha: "ha_NG", ig: "ig_NG", pcm: "pcm_NG", mr: "mr_IN", vi: "vi_VN", ta: "ta_IN", te: "te_IN", tr: "tr_TR", is: "is_IS", sv: "sv_SE", it: "it_IT", pl: "pl_PL", ro: "ro_RO", nl: "nl_NL" }[lang] },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "index, follow, max-image-preview:large" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Person",
              "@id": "https://www.luizajesusdoprado.com/#person",
              name: "Luiza Jesus do Prado",
              alternateName: ["MAVRA", "Luiza Jesus Prado", "Luiza Prado", "Hifa Cybe", "Poteh Pehuono"],
              disambiguatingDescription:
                "Luiza Jesus do Prado (performance name MAVRA) is a Brazilian transdisciplinary artist and researcher in political art, art, science and technology, ritual and decolonial practice; earlier works were credited as Luiza Prado, Hifa Cybe and Poteh Pehuono.",
              url: "https://www.luizajesusdoprado.com/",
              jobTitle: "Brazilian transdisciplinary and political artist, researcher in art, science and technology",
              description:
                "Brazilian political artist, researcher and activist for the decolonization of mental health, working across art, science and technology, sexuality and gender, psilocybin research, ritual and Indigenous knowledge. Exhibited at PST: LA/LA (USA), the Casablanca Biennale and the Shanghai Biennial of Photography and Video, with works at MAC Bogotá.",
              nationality: { "@type": "Country", name: "Brazil" },
              homeLocation: [
                { "@type": "Place", name: "São Paulo, Brazil" },
                { "@type": "Place", name: "Berlin, Germany" },
                { "@type": "Place", name: "Madrid, Spain" },
              ],
              hasOccupation: [
                { "@type": "Occupation", name: "Visual artist" },
                { "@type": "Occupation", name: "Performance artist" },
                { "@type": "Occupation", name: "Activist for the decolonization of mental health" },
                { "@type": "Occupation", name: "Researcher in art, science and technology" },
              ],
              sameAs: [
                "https://www.instagram.com/luizajesusdoprado",
                "https://www.instagram.com/mavrayvyuna",
                "https://www.mavrayvyuna.com",
                "https://www.luizaprado.com",
                "https://www.luizaprado.cc",
                "https://open.spotify.com/artist/0NzpT3pNycVS6uoFKGUk7R",
                "https://open.spotify.com/artist/3pNVBkJAivjuYrz8gcA4S9",
                "https://music.apple.com/es/artist/luiza-prado/1536796715",
                "https://music.apple.com/fr/artist/hifa-cybe/1546631802",
                "https://www.deezer.com/br/artist/10584853",
                "https://flaviagoa.bandcamp.com",
              ],
              knowsAbout: [
                "Contemporary art",
                "Brazilian art",
                "Political art",
                "Art, science and technology",
                "Mental health",
                "Decolonization",
                "Decolonization of mental health",
                "Mental health activism",
                "Sexuality",
                "Gender",
                "Trans and queer art",
                "Decolonial art",
                "Ritual",
                "Psilocybin",
                "Psilocybin research",
                "Indigenous knowledge",
                "Performance art",
                "Butoh",
                "Photography",
                "Sound art",
                "Electronic music",
                "Installation art",
                "Memory research",
                "Expanded photography",
                "Video art",
                "Art and technology",
                "Science and art",
                "Neuroscience",
                "Psychobiology",
                "Semiotics",
                "Public health",
                "Decolonial healing",
                "Gender and sexuality",
                "Post-human body",
                "Prosthetics and robotics in art",
                "Body as technology",
                "Cognitive technologies",
                "Social hacking",
                "Counter-archive",
              ],
            },
            {
              "@type": "WebSite",
              "@id": "https://www.luizajesusdoprado.com/#website",
              url: "https://www.luizajesusdoprado.com/",
              name: "Luiza Jesus do Prado",
              inLanguage: lang,
              publisher: { "@id": "https://www.luizajesusdoprado.com/#person" },
            },
          ],
        }),
      },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Archivo:wdth,wght@62..125,300..900&family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400;1,500&family=Schibsted+Grotesk:wght@300;400;500;600;800&family=IBM+Plex+Mono:wght@300;400;500&display=swap",
      },
      { rel: "icon", href: "/favicon.png", type: "image/png" },
      ...(matches.length > 1 ? langLinks(leaf?.pathname ?? "/", lang) : []),
    ],
    };
  },

  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

/** Names the language on <html> before the page paints, straight from the address. */
const HTML_LANG =
  "var m=/^\\/(pt|es|de|fr|ja|he|ar|zh|hi|ru|sw|yo|ha|ig|pcm|mr|vi|ta|te|tr|is|sv|it|pl|ro|nl)(?=\\/|$)/.exec(location.pathname),l=m?m[1]:'en',h=document.documentElement;h.lang=l;h.dir=l==='he'||l==='ar'?'rtl':'ltr';";

function RootShell({ children }: { children: ReactNode }) {
  const router = useRouter();
  // The shell renders before the language provider; follow this address's language.
  setRenderLang(typeof document !== "undefined" ? document.documentElement.lang || router.$lang.get() : router.$lang.get());
  return (
    <html lang={router.$lang.get()} dir={RTL_LANGS.includes(router.$lang.get()) ? "rtl" : "ltr"} suppressHydrationWarning>
      <head>
        <LocalizedHeadContent />
        <meta name="google-site-verification" content="kqe76r-OvoNPpAI3ayLjGuBZpgv_EpP7cdWoJNOpEzM" />
        <script dangerouslySetInnerHTML={{ __html: HTML_LANG }} />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const isLab = useRouterState({ select: (state) => state.location.pathname.startsWith("/lab/") });

  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
          <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:bg-bone focus:px-4 focus:py-3 focus:text-ink">
          Skip to content
          </a>
        {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
        <Outlet />
        {isLab ? null : <BackButton />}
        {isLab ? null : <ImageLightbox />}
        {isLab ? null : <LangOffer />}
        {isLab ? null : <AccessibilityMenu />}
      </I18nProvider>
    </QueryClientProvider>
  );
}
